<!-- GitHub-style home: 3 rails. Desktop = fixed viewport, the feed scrolls. -->
<div class="bg-[#fbf9f9] dark:bg-neutral-950 lg:h-[calc(100dvh-56px)] overflow-y-auto lg:overflow-hidden">
  <div class="mx-auto w-full max-w-[1400px] flex flex-col lg:flex-row gap-4 lg:gap-6 px-4 lg:px-8 py-4 lg:py-6 lg:h-full">

    <!-- ─────────────── LEFT RAIL: org + workspaces ─────────────── -->
    <aside class="w-full lg:w-[300px] lg:shrink-0 flex flex-col gap-4 lg:h-full lg:overflow-y-auto pb-1">

      @if (sessionService.activeOrganization(); as org) {
        <section class="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-xl overflow-hidden">
          <!-- Org header -->
          <div class="flex items-center gap-3 px-4 py-3.5 border-b border-neutral-100 dark:border-neutral-800">
            <app-identicon [seed]="org.id" class="h-8 w-8 rounded-md shrink-0" />
            <div class="min-w-0 flex-1">
              <p class="text-[11px] font-medium uppercase tracking-wide text-neutral-400 dark:text-neutral-500 leading-none mb-0.5">Organization</p>
              <p class="font-semibold text-sm text-neutral-900 dark:text-white truncate leading-tight">{{ org.name }}</p>
            </div>
            <button
              (click)="navigateToOrgSettings()"
              title="Organization settings"
              class="shrink-0 text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200 transition-colors"
            >
              <i class="pi pi-cog text-base"></i>
            </button>
          </div>

          <!-- Workspaces -->
          <div class="px-3 py-3">
            <div class="flex items-center justify-between px-1 mb-2">
              <span class="text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Workspaces</span>
              <button
                (click)="openCreateWorkspaceDialog()"
                class="flex items-center gap-1 text-xs font-medium text-[#1890ff] hover:text-blue-700 transition-colors"
              >
                <i class="pi pi-plus text-[10px]"></i> New
              </button>
            </div>

            <div class="flex flex-col gap-0.5 max-h-[280px] lg:max-h-none overflow-y-auto">
              @for (ws of workspacesForActiveOrganization(); track ws.id) {
                <button
                  (click)="onSelectWorkspace(ws)"
                  class="group flex items-center gap-2.5 w-full px-2 py-2 rounded-lg text-left hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
                >
                  <span class="flex items-center justify-center size-7 shrink-0 rounded-md bg-orange-50 dark:bg-orange-900/20 text-[#F97002]">
                    <i class="pi pi-folder text-sm"></i>
                  </span>
                  <span class="flex-1 min-w-0 text-sm font-medium text-neutral-800 dark:text-neutral-200 truncate">{{ ws.name }}</span>
                  <i class="pi pi-arrow-up-right text-xs text-neutral-300 dark:text-neutral-600 group-hover:text-[#F97002] transition-colors"></i>
                </button>
              } @empty {
                <div class="px-2 py-4 text-center">
                  <p class="text-sm text-neutral-500 dark:text-neutral-400 mb-2">No workspaces yet.</p>
                  <button
                    (click)="openCreateWorkspaceDialog()"
                    class="text-xs font-medium text-[#1890ff] hover:text-blue-700 transition-colors"
                  >
                    + Create your first workspace
                  </button>
                </div>
              }
            </div>
          </div>
        </section>
      } @else {
        <section class="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-xl p-4 flex flex-col gap-2">
          <p-skeleton height="2rem" />
          <p-skeleton height="2rem" />
          <p-skeleton height="2rem" />
        </section>
      }

      <!-- Org footer link -->
      <button
        (click)="navigateToOrganizations()"
        class="flex items-center justify-center gap-1.5 text-sm font-medium text-[#1890ff] hover:text-blue-700 transition-colors py-1"
      >
        <i class="pi pi-plus text-xs"></i> New organization
      </button>
    </aside>

    <!-- ─────────────── CENTER RAIL: recent activity feed ─────────────── -->
    <main class="w-full lg:flex-1 lg:min-w-0 flex flex-col gap-4 lg:h-full">

      <!-- Greeting + primary CTA -->
      <section class="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-xl px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 shrink-0">
        <div class="flex-1 min-w-0">
          <h1 class="font-semibold text-lg text-neutral-900 dark:text-white leading-tight truncate">
            Welcome back, {{ sessionService.profile()?.display_name || 'there' }}
          </h1>
          <p class="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">Pick up where you left off, or start something new.</p>
        </div>
        <button
          (click)="startNewTechVector()"
          class="shrink-0 inline-flex items-center justify-center gap-2 px-4 h-10 rounded-lg bg-[#F97002] hover:bg-[#d45f00] active:bg-orange-700 text-white font-medium text-sm transition-colors"
        >
          <i class="pi pi-plus text-sm"></i>
          New Tech Vector
        </button>
      </section>

      <!-- Activity feed (borderless, GitHub-style) -->
      <section class="flex flex-col lg:flex-1 lg:min-h-0">

        <!-- Header + filters -->
        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pb-2.5 mb-1 border-b border-neutral-200 dark:border-neutral-800 shrink-0">
          <span class="font-semibold text-sm text-neutral-900 dark:text-white">Recent activity</span>
          <div class="flex items-center gap-1 overflow-x-auto">
            @for (f of [
              { key: 'all', label: 'All', icon: '' },
              { key: 'tv', label: 'Tech Vectors', icon: 'pi-list' },
              { key: 'reports', label: 'Reports', icon: 'pi-chart-bar' },
              { key: 'budget', label: 'Budget', icon: 'pi-dollar' }
            ]; track f.key) {
              <button
                (click)="activeFilter.set($any(f.key))"
                class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium whitespace-nowrap transition-colors"
                [class]="activeFilter() === f.key
                  ? 'bg-neutral-200/70 dark:bg-neutral-800 text-neutral-900 dark:text-white'
                  : 'text-neutral-500 dark:text-neutral-400 hover:text-neutral-800 dark:hover:text-neutral-200'"
              >
                @if (f.icon) { <i class="pi {{ f.icon }} text-[10px]"></i> }
                {{ f.label }}
              </button>
            }
          </div>
        </div>

        <!-- Feed -->
        <div class="lg:flex-1 lg:min-h-0 overflow-y-auto -mx-2">
          @if (activityLoading()) {
            <div class="flex flex-col">
              @for (i of [1,2,3,4,5]; track i) {
                <div class="flex items-center gap-3 px-2 py-3">
                  <p-skeleton shape="circle" size="2.5rem" />
                  <div class="flex-1 flex flex-col gap-1.5">
                    <p-skeleton height="0.9rem" width="60%" />
                    <p-skeleton height="0.75rem" width="35%" />
                  </div>
                </div>
              }
            </div>
          } @else {
            @for (item of filteredActivity(); track item.kind + item.id) {
              <button
                (click)="onOpenActivityItem(item)"
                class="group flex items-center gap-3 w-full px-2 py-3 text-left border-b border-neutral-100 dark:border-neutral-800/70 hover:bg-neutral-100/60 dark:hover:bg-neutral-800/40 transition-colors"
              >
                <!-- Icon / thumbnail -->
                <div class="shrink-0 size-10 rounded-lg overflow-hidden flex items-center justify-center"
                     [class]="item.kind === 'tv' ? 'bg-orange-50 dark:bg-orange-900/20' : item.kind === 'report' ? 'bg-blue-50 dark:bg-blue-900/20' : 'bg-emerald-50 dark:bg-emerald-900/20'">
                  @if (item.kind === 'tv' && item.workspaceId) {
                    <app-sb-image
                      [path]="tvService.getTechVectorImagePath(item.id, item.workspaceId)"
                      [alt]="item.title"
                      class="w-full h-full"
                      imgClass="w-full h-full object-cover"
                    />
                  } @else if (item.kind === 'report') {
                    <i class="pi pi-chart-bar text-blue-400 text-base"></i>
                  } @else if (item.kind === 'budget') {
                    <i class="pi pi-dollar text-emerald-500 text-base"></i>
                  } @else {
                    <i class="pi pi-list text-[#F97002] text-base"></i>
                  }
                </div>

                <!-- Text -->
                <div class="flex-1 min-w-0">
                  <p class="font-medium text-sm text-neutral-900 dark:text-white truncate">{{ item.title }}</p>
                  <div class="flex items-center gap-2 text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                    <span class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-neutral-100 dark:bg-neutral-800 font-medium capitalize">
                      {{ item.kind === 'tv' ? 'Tech Vector' : item.kind }}
                    </span>
                    @if (item.subtitle) {
                      <span class="truncate">{{ item.subtitle }}</span>
                      <span class="text-neutral-300 dark:text-neutral-600">·</span>
                    }
                    <span class="whitespace-nowrap">{{ item.date | date: 'MMM d, y' }}</span>
                  </div>
                </div>

              @if (openingItemId() === item.id) {
                <i class="pi pi-spinner pi-spin text-[#F97002] text-sm shrink-0"></i>
              } @else {
                <i class="pi pi-chevron-right text-neutral-300 dark:text-neutral-600 group-hover:text-neutral-500 text-xs shrink-0"></i>
              }
            </button>
          } @empty {
            <div class="flex flex-col items-center text-center px-4 pt-10 pb-6 gap-2">
                <span class="flex items-center justify-center size-12 rounded-full bg-neutral-100 dark:bg-neutral-800 mb-1">
                  @if (activeFilter() === 'reports') {
                    <i class="pi pi-chart-bar text-xl text-neutral-400"></i>
                  } @else if (activeFilter() === 'budget') {
                    <i class="pi pi-dollar text-xl text-neutral-400"></i>
                  } @else {
                    <i class="pi pi-inbox text-xl text-neutral-400"></i>
                  }
                </span>
                <p class="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                  @if (activeFilter() === 'tv') { No tech vectors yet }
                  @else if (activeFilter() === 'reports') { No reports yet }
                  @else if (activeFilter() === 'budget') { No budget searches yet }
                  @else { No recent activity yet }
                </p>
                <p class="text-xs text-neutral-400 dark:text-neutral-500 max-w-[260px]">
                  @if (activeFilter() === 'budget') { Your saved budget searches will appear here. }
                  @else if (activeFilter() === 'reports') { Reports generated from tech vectors will appear here. }
                  @else { Start a tech vector to see your work show up here. }
                </p>
                @if (activeFilter() === 'all' || activeFilter() === 'tv') {
                  <button
                    (click)="startNewTechVector()"
                    class="mt-2 inline-flex items-center gap-2 px-3 h-9 rounded-lg bg-[#F97002] hover:bg-[#d45f00] text-white font-medium text-sm transition-colors"
                  >
                    <i class="pi pi-plus text-xs"></i> New Tech Vector
                  </button>
                }
              </div>
            }
          }
        </div>
      </section>
    </main>

    <!-- ─────────────── RIGHT RAIL: notifications (inbox) ─────────────── -->
    <aside class="w-full lg:w-[340px] lg:shrink-0 flex flex-col lg:h-full">
      <section class="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-xl flex flex-col lg:h-full overflow-hidden">
        <!-- Header -->
        <div class="flex items-center justify-between gap-2 px-4 py-3 border-b border-neutral-100 dark:border-neutral-800 shrink-0">
          <div class="flex items-center gap-2 min-w-0">
            <i class="pi pi-bell text-[#F97002] text-sm"></i>
            <span class="font-semibold text-sm text-neutral-900 dark:text-white">Notifications</span>
            @if (numUnread() > 0) {
              <span class="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#F97002] text-white text-[10px] font-semibold">{{ numUnread() }}</span>
            }
          </div>
          <div class="flex items-center bg-neutral-100 dark:bg-neutral-800 rounded-md p-0.5 shrink-0">
            <button
              (click)="inboxFilter.set('unread')"
              class="px-2 py-0.5 rounded text-[11px] font-medium transition-colors"
              [class]="inboxFilter() === 'unread' ? 'bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white shadow-sm' : 'text-neutral-500 dark:text-neutral-400'"
            >Unread</button>
            <button
              (click)="inboxFilter.set('all')"
              class="px-2 py-0.5 rounded text-[11px] font-medium transition-colors"
              [class]="inboxFilter() === 'all' ? 'bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white shadow-sm' : 'text-neutral-500 dark:text-neutral-400'"
            >All</button>
          </div>
        </div>

        <!-- List -->
        <div class="flex-1 lg:min-h-0 overflow-y-auto p-2 flex flex-col gap-1">
          @for (item of inboxItems(); track item.id) {
            <div
              (click)="onClickNotification(item)"
              class="group relative rounded-lg p-3 transition-colors"
              [class]="(inboxService.isActionable(item) ? 'cursor-pointer ' : '') + (item.status === 'UNREAD' ? 'bg-orange-50/50 dark:bg-orange-900/10 hover:bg-orange-50 dark:hover:bg-orange-900/20' : 'hover:bg-neutral-50 dark:hover:bg-neutral-800/60')"
            >
              <div class="flex items-start gap-2.5">
                <span class="flex items-center justify-center size-8 shrink-0 rounded-md" [class]="notificationIconClass(item)">
                  <i [class]="notificationIcon(item) + ' text-sm'"></i>
                </span>
                <div class="flex-1 min-w-0">
                  <div class="flex items-start gap-2">
                    <p class="flex-1 min-w-0 font-medium text-sm text-neutral-900 dark:text-white leading-snug">{{ item.title }}</p>
                    @if (item.status === 'UNREAD') {
                      <span class="mt-1 size-2 shrink-0 rounded-full bg-[#F97002]"></span>
                    }
                  </div>
                  @if (item.description) {
                    <p class="text-xs text-neutral-500 dark:text-neutral-400 leading-relaxed line-clamp-2 mt-0.5">{{ item.description }}</p>
                  }
                  <div class="flex items-center justify-between gap-2 mt-1.5">
                    <span class="text-[11px] text-neutral-400 dark:text-neutral-500 whitespace-nowrap">{{ item.created_at | date: 'MMM d, y' }}</span>
                    <div class="flex items-center gap-1">
                      @if (inboxService.isActionable(item)) {
                        <span class="text-[11px] font-medium text-[#1890ff] group-hover:underline">{{ item.action_label || 'View' }}</span>
                      }
                      @if (item.status === 'UNREAD') {
                        <button
                          (click)="markNotificationRead($event, item)"
                          title="Mark as read"
                          class="text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200 transition-colors p-0.5"
                        ><i class="pi pi-check text-xs"></i></button>
                      } @else {
                        <button
                          (click)="archiveNotification($event, item)"
                          title="Archive"
                          class="text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200 transition-colors p-0.5"
                        ><i class="pi pi-inbox text-xs"></i></button>
                      }
                    </div>
                  </div>
                </div>
              </div>
            </div>
          } @empty {
            <div class="flex flex-col items-center justify-center h-full min-h-[160px] text-center gap-2 px-4">
              <span class="flex items-center justify-center size-12 rounded-full bg-neutral-100 dark:bg-neutral-800 mb-1">
                <i class="pi pi-bell text-xl text-neutral-400"></i>
              </span>
              <p class="text-sm font-medium text-neutral-600 dark:text-neutral-300">
                {{ inboxFilter() === 'unread' ? 'You’re all caught up' : 'No notifications' }}
              </p>
              <p class="text-xs text-neutral-400 dark:text-neutral-500">
                {{ inboxFilter() === 'unread' ? 'New notifications will show up here.' : 'Notifications will appear here.' }}
              </p>
            </div>
          }
        </div>

        <!-- Footer -->
        @if (numUnread() > 0) {
          <div class="border-t border-neutral-100 dark:border-neutral-800 px-3 py-2 shrink-0">
            <button
              (click)="markAllNotificationsRead()"
              class="w-full text-center text-xs font-medium text-[#1890ff] hover:text-blue-700 transition-colors py-1"
            >
              Mark all as read
            </button>
          </div>
        }
      </section>
    </aside>

  </div>
</div>

<!-- ─────────────── Workspace picker (gates new TV) ─────────────── -->
<p-dialog
  [(visible)]="workspacePickerVisible"
  [modal]="true"
  [draggable]="false"
  [dismissableMask]="true"
  [closeOnEscape]="true"
  [style]="{ width: '30rem', maxWidth: '92vw' }"
>
  <ng-template #header>
    <div class="flex flex-col">
      <span class="text-lg font-semibold text-neutral-900 dark:text-neutral-100">Select a workspace</span>
      <span class="text-sm text-neutral-500 dark:text-neutral-400">Choose where this Tech Vector should live.</span>
    </div>
  </ng-template>

  <div class="flex flex-col gap-1 max-h-72 overflow-y-auto -mx-1 px-1">
    @for (ws of workspacesForActiveOrganization(); track ws.id) {
      <button
        (click)="onPickWorkspace(ws)"
        class="group flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-left border border-transparent hover:border-[#F97002]/40 hover:bg-orange-50/60 dark:hover:bg-orange-900/10 transition-colors"
      >
        <span class="flex items-center justify-center size-8 shrink-0 rounded-md bg-orange-50 dark:bg-orange-900/20 text-[#F97002]">
          <i class="pi pi-folder text-sm"></i>
        </span>
        <span class="flex-1 min-w-0 text-sm font-medium text-neutral-800 dark:text-neutral-200 truncate">{{ ws.name }}</span>
        <i class="pi pi-chevron-right text-xs text-neutral-300 dark:text-neutral-600 group-hover:text-[#F97002] transition-colors"></i>
      </button>
    }
  </div>

  <ng-template #footer>
    <div class="flex items-center justify-between w-full">
      <button
        (click)="workspacePickerVisible.set(false); openCreateWorkspaceDialog()"
        class="text-sm font-medium text-[#1890ff] hover:text-blue-700 transition-colors"
      >
        + New workspace
      </button>
      <p-button label="Cancel" severity="secondary" [text]="true" (click)="workspacePickerVisible.set(false)" />
    </div>
  </ng-template>
</p-dialog>

<!-- ─────────────── Create workspace dialog ─────────────── -->
<p-dialog
  [(visible)]="createWorkspaceDialogVisible"
  [modal]="true"
  [draggable]="false"
  [dismissableMask]="true"
  [closeOnEscape]="true"
  [style]="{ width: '30rem', maxWidth: '92vw' }"
>
  <ng-template #header>
    <div class="text-lg font-semibold text-neutral-900 dark:text-neutral-100">Create Workspace</div>
  </ng-template>

  <div class="space-y-4">
    <div class="text-sm text-neutral-600 dark:text-neutral-300">
      Create a workspace to organize your tech vectors and reports.
    </div>
    <div class="space-y-2">
      <label class="text-sm font-medium text-neutral-700 dark:text-neutral-200">Workspace name</label>
      <input
        pInputText
        [ngModel]="createWorkspaceName()"
        (ngModelChange)="createWorkspaceName.set($event ?? '')"
        (keyup.enter)="createWorkspace()"
        placeholder="e.g., Main Workspace"
        class="w-full"
      />
    </div>
    <div class="flex items-center gap-2">
      <p-checkbox
        [ngModel]="createWorkspaceIsDefault()"
        (ngModelChange)="createWorkspaceIsDefault.set(!!$event)"
        [binary]="true"
        inputId="ws2-default"
      />
      <label for="ws2-default" class="text-sm text-neutral-700 dark:text-neutral-200">
        Set as default workspace
      </label>
    </div>
    @if (createWorkspaceError()) {
      <div class="text-sm text-red-500">{{ createWorkspaceError() }}</div>
    }
  </div>

  <ng-template #footer>
    <div class="flex justify-end gap-2">
      <p-button label="Cancel" severity="secondary" [text]="true" (click)="createWorkspaceDialogVisible.set(false)" />
      <p-button
        label="Create Workspace"
        icon="pi pi-check"
        [loading]="createWorkspaceLoading()"
        [disabled]="!createWorkspaceName().trim() || createWorkspaceLoading()"
        (click)="createWorkspace()"
      />
    </div>
  </ng-template>
</p-dialog>
